<template>
  <b-modal
    :title="$t('menubar.CatalogGen')"
    id="catalog-generate-modal"
    header-close-content=""
    size="xl"
    body-class="advanced-search-modal"
    v-model="localModel"
    @ok.prevent="nextOrGenerate"
    @cancel="hideDialog"
    @close="hideDialog"
    no-close-on-backdrop
    no-close-on-esc>
    <div v-if="props.sourceType == SourceType.Filter">
      <CatalogSelection
        ref="catalogSelection"
        v-if="vm.isCatalogSelection"
        v-model:sourceCatalogId="vm.sourceCatalogId"
        v-model:targetCatalogName="vm.targetCatalogName" />
      <AdvancedSearch
        ref="advancedSearch"
        v-if="vm.isLastPage"
        :hideExcelEdit="true"
        :catalogId="vm.sourceCatalogId"
        v-model:advancedSearchId="vm.advancedSearchId" />

      <div v-if="vm.isGeneratingCatalog">
        <b-overlay z-index="20" opacity="1" variant="white" no-wrap :show="vm.isGeneratingCatalog">
          <template #overlay>
            <div class="mt-3 text-center">
              {{ $t("CatalogGenerationRunningInBackground") }}
            </div>
            <spinner size="48" />
          </template>
        </b-overlay>
      </div>
    </div>

    <template #modal-footer="{ ok, cancel }">
      <div class="left-items">
        <b-btn v-if="vm.isLastPage" :disabled="vm.isGeneratingCatalog" @click="back">{{ $t("back") }}</b-btn>
      </div>
      <div class="right-items">
        <b-btn :disabled="!isModalValid || vm.isGeneratingCatalog" @click.prevent="ok">{{ !vm.isLastPage ? $t("next") : $t("generate") }}</b-btn>
        <b-btn @click="cancel">{{ $t("Close") }}</b-btn>
      </div>
    </template>
  </b-modal>
</template>

<script setup lang="ts">
import spinner from "@common/components/Spinner.vue";
import { useToast } from "@common/composable/toast";
import { useContentLanguages } from "@common/composable/useMetaDataStore";
import { SourceType } from "@medapro-types/ChannelCreator/channel-creator-enum";
import catalogApi from "@services/catalogs.js";
import { computed, inject, onBeforeUnmount, onMounted, reactive, ref, unref, watch } from "vue";
import { useI18n } from "vue-i18n";
import AdvancedSearch from "../../../AdvancedSearch/views/AdvancedSearch.vue";
import CatalogSelection from "./GenerateCatalog/CatalogSelection.vue";

//#region Inject
const $getConnectionId = inject<() => string | null>("$getConnectionId");
const $subscribeToServerEvent = inject<(event: string, handler: any) => void>("$subscribeToServerEvent");
const $unsubscribeFromServerEvent = inject<(event: string, handler: any) => void>("$unsubscribeFromServerEvent");
const $signalRStartedPromise = inject<Promise<void>>("$signalRStartedPromise");
//#endregion

//#region General
const { createToast } = useToast();
const props = defineProps<{ value: boolean; sourceType: SourceType }>();
const emits = defineEmits(["input"]);
const { primaryLanguageCode } = useContentLanguages();
const { t: $t, te: $te } = useI18n();

const localModel = computed({
  get() {
    return props.value;
  },
  set(nv) {
    emits("input", nv);
  }
});
//#endregion

//#region viewmodel
type ViewModel = {
  sourceCatalogId: string;
  targetCatalogName: string;
  errorMessage: string;
  advancedSearchId: string;
  isCatalogSelection: boolean;
  isGeneratingCatalog: boolean;
  isLastPage: boolean;
};

const vm = reactive<ViewModel>({
  sourceCatalogId: "",
  targetCatalogName: "",
  errorMessage: "",
  advancedSearchId: "",
  isCatalogSelection: true,
  isGeneratingCatalog: false,
  isLastPage: false
});
//#endregion

//#region Refs
const advancedSearch = ref<InstanceType<typeof AdvancedSearch> | null>(null);
const catalogSelection = ref<InstanceType<typeof CatalogSelection> | null>(null);
//#endregion

const isModalValid = computed(() => {
  if (props.sourceType == SourceType.Filter) {
    const advSearchObj = unref(advancedSearch);
    const catalogSelectionObj = unref(catalogSelection);
    return (
      (vm.isCatalogSelection && vm.sourceCatalogId && vm.targetCatalogName && catalogSelectionObj?.state.isValid) ||
      (vm.isLastPage && vm.sourceCatalogId && vm.targetCatalogName && vm.advancedSearchId && !advSearchObj?.states?.isDirty)
    );
  } else {
    return true;
  }
});

const nextOrGenerate = async () => {
  if (vm.isCatalogSelection && !vm.isLastPage) {
    vm.isCatalogSelection = false;
    vm.isLastPage = true;
  } else {
    if (props.sourceType == SourceType.Filter) {
      try {
        vm.isGeneratingCatalog = true;
        await catalogApi.generateCatalogFromFilter(
          vm.sourceCatalogId,
          vm.targetCatalogName,
          vm.advancedSearchId,
          primaryLanguageCode.value,
          $getConnectionId ? ($getConnectionId() ?? "") : ""
        );
      } catch (error: any) {
        if (error) {
          console.error(`Catalog generation failure  with error - "${error}" `);
          let errorMessage = "";
          if ($te("ErrorMessage." + error.response.data?.value)) errorMessage = $t("ErrorMessage." + error.response.data?.value);
          else errorMessage = error.response.data?.value;
          createToast({
            text: errorMessage,
            title: $t("ErrorMessage.CatalogueGenerationFailed"),
            variant: "warning",
            autoClose: false
          });
        }
      }
    }
  }
};

const back = () => {
  vm.isCatalogSelection = true;
  vm.isLastPage = false;
};

const hideDialog = () => {
  clearDialog();
  localModel.value = false;
};

const clearDialog = () => {
  vm.isCatalogSelection = true;
  vm.isLastPage = false;
  vm.advancedSearchId = "";
  vm.sourceCatalogId = "";
  vm.targetCatalogName = "";
};

watch(
  () => localModel.value,
  (nv) => {
    if (!nv && $unsubscribeFromServerEvent) {
      unSubscribeEvent();
    }
  },
  { deep: true }
);

function onCatalogGenerationFromFilterCompleted() {
  vm.isGeneratingCatalog = false;
  clearDialog();
  localModel.value = false;
}

async function startEventSubscription() {
  await $signalRStartedPromise;
  if ($subscribeToServerEvent) {
    $subscribeToServerEvent("CatalogGenerationFromFilterCompleted", onCatalogGenerationFromFilterCompleted);
  }
}

async function unSubscribeEvent() {
  if ($unsubscribeFromServerEvent) {
    $unsubscribeFromServerEvent("CatalogGenerationFromFilterCompleted", onCatalogGenerationFromFilterCompleted);
  }
}

//#region LifeCycle
onMounted(() => {
  startEventSubscription();
  clearDialog();
});

onBeforeUnmount(() => {
  unSubscribeEvent();
});
//#endregion
</script>
